import { Fragment, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { Box, Divider, Typography } from '@mui/material';
import { Plus } from 'lucide-react';

import type { UserCard_UserFragment } from '@graphql/__generated__/graphql';
import { CardHeaderIconActions } from '@modules/_shared/components/CardHeaderIconActions';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import {
  EditableUserCard,
  UserActionCard,
} from '@modules/_shared/components/UserActionCard';
import { useLocale } from '@shared/hooks/locale';

interface SellersActionCardProps {
  isLoading?: boolean;
  /** Rendered above the seller list (e.g. the FSBO advertiser's standing). */
  children?: React.ReactNode;
  sellers: UserCard_UserFragment[];
  openedDefault?: boolean;
  readOnly?: boolean;
  onSellersChanged: (
    sellerIds: string[],
    onError: (err: ErrorLike) => void,
  ) => Promise<any>;
}

export const SellersActionCardSkeleton: React.FC<{
  openedDefault?: boolean;
}> = ({ openedDefault = false }) => {
  const { t } = useLocale();

  return (
    <ListingAccordion title={t('Sellers')} defaultExpanded={openedDefault}>
      <UserActionCard
        isLoading
        user={null}
        onUserChanged={() => Promise.resolve()}
      />
    </ListingAccordion>
  );
};

export const SellersActionCard: React.FC<SellersActionCardProps> = ({
  sellers,
  onSellersChanged,
  isLoading = false,
  openedDefault = false,
  readOnly = false,
  children,
}) => {
  const { t } = useLocale();
  const [displayAddSeller, setDisplayAddSeller] = useState(false);

  if (isLoading) {
    return <SellersActionCardSkeleton openedDefault={openedDefault} />;
  }

  const sellerIds = sellers.map(s => s.id);

  return (
    <ListingAccordion
      title={t('Sellers')}
      defaultExpanded={openedDefault}
      headerAction={
        readOnly ? undefined : (
          <CardHeaderIconActions
            actions={[
              {
                label: t('Add seller'),
                icon: <Plus size={16} />,
                onClick: () => setDisplayAddSeller(true),
                disabled: displayAddSeller,
              },
            ]}
          />
        )
      }
    >
      {children ? <Box sx={{ mb: 2 }}>{children}</Box> : null}
      {sellers.map((seller, index) => (
        <Fragment key={seller.id}>
          <UserActionCard
            user={seller}
            creatable
            readOnly={readOnly}
            canDelete={!readOnly}
            onUserChanged={(userId, onError) => {
              const newIds = sellerIds.filter(id => id !== seller.id);

              if (userId) {
                newIds.push(userId);
              }

              return onSellersChanged(newIds, onError);
            }}
          />
          {index < sellers.length - 1 && <Divider sx={{ my: 2 }} />}
        </Fragment>
      ))}

      {sellers.length === 0 && !displayAddSeller && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('No sellers assigned')}
        </Typography>
      )}

      {!readOnly && displayAddSeller && (
        <Box sx={{ mt: sellers.length > 0 ? 2 : 0 }}>
          <EditableUserCard
            creatable
            isEditing
            onChange={async userId => {
              const newIds = [...sellerIds, userId];

              await onSellersChanged(newIds, err => console.error(err));
              setDisplayAddSeller(false);
            }}
            onCancel={() => setDisplayAddSeller(false)}
          />
        </Box>
      )}
    </ListingAccordion>
  );
};
